import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ShieldBan, Ban, Search, Loader2, FileSpreadsheet, FileText, FileDown, RefreshCw, ListChecks,
} from 'lucide-react';

const fechaCorta = (f) => (f ? String(f).replace('T', ' ').slice(0, 19) : '—');

const ResultadoBadge = ({ v }) => {
  if (v === 'OK') return <Badge className="bg-emerald-100 text-emerald-700 border-0">OK</Badge>;
  if (v === 'Error') return <Badge className="bg-rose-100 text-rose-700 border-0">Error</Badge>;
  return <Badge className="bg-slate-100 text-slate-500 border-0">Pendiente</Badge>;
};

const WayaBloqueosHistorial = () => {
  const [rows, setRows] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState('');
  const [f, setF] = useState({ fecha_desde: '', fecha_hasta: '', tipo: 'todos', origen: 'todos', q: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(f).forEach(([k, v]) => { if (v && v !== 'todos') params[k] = v; });
      const r = await api.get('/waya-movimientos/bloqueos-historial', { params });
      setRows(r.data?.rows || []);
      setResumen(r.data?.resumen || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cargar el historial de bloqueos');
    } finally {
      setLoading(false);
    }
  }, [f]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const exportar = async (formato) => {
    setExporting(formato);
    try {
      const params = { formato };
      Object.entries(f).forEach(([k, v]) => { if (v && v !== 'todos') params[k] = v; });
      const r = await api.get('/waya-movimientos/bloqueos-historial/export', { params, responseType: 'blob' });
      const ext = formato === 'xlsx' ? 'xlsx' : formato;
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `bloqueos_cierres.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exportado a ${formato.toUpperCase()}`);
    } catch (e) {
      toast.error('No se pudo exportar');
    } finally {
      setExporting('');
    }
  };

  return (
    <div data-testid="waya-bloq-panel">
      {/* Encabezado + resumen */}
      <div className="flex items-center gap-2 mb-3">
        <ShieldBan size={18} className="text-slate-500" />
        <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">Bloqueos y cierres ejecutados</h2>
      </div>

      {resumen && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="waya-bloq-resumen">
          <Badge className="bg-slate-100 text-slate-700 text-sm px-3 py-1"><ListChecks size={13} className="mr-1.5" /> Total: {resumen.total}</Badge>
          <Badge className="bg-blue-600 text-white text-sm px-3 py-1">Bloqueos: {resumen.bloqueos}</Badge>
          <Badge className="bg-red-600 text-white text-sm px-3 py-1">Cierres: {resumen.cierres}</Badge>
          <Badge className="bg-slate-100 text-slate-600 text-sm px-3 py-1">Automáticos: {resumen.automaticos}</Badge>
          <Badge className="bg-slate-100 text-slate-600 text-sm px-3 py-1">Manuales: {resumen.manuales}</Badge>
        </div>
      )}

      {/* Filtros + export */}
      <Card className="p-3 mb-3">
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="text-[11px] text-slate-500 block mb-1">Desde</label>
            <Input type="date" value={f.fecha_desde} onChange={(e) => setF(s => ({ ...s, fecha_desde: e.target.value }))} className="h-9 w-40" data-testid="waya-bloq-filtro-desde" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500 block mb-1">Hasta</label>
            <Input type="date" value={f.fecha_hasta} onChange={(e) => setF(s => ({ ...s, fecha_hasta: e.target.value }))} className="h-9 w-40" data-testid="waya-bloq-filtro-hasta" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500 block mb-1">Tipo</label>
            <Select value={f.tipo} onValueChange={(v) => setF(s => ({ ...s, tipo: v }))}>
              <SelectTrigger className="h-9 w-44" data-testid="waya-bloq-filtro-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="bloqueo">Bloqueo preventivo</SelectItem>
                <SelectItem value="cierre">Cierre definitivo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] text-slate-500 block mb-1">Origen</label>
            <Select value={f.origen} onValueChange={(v) => setF(s => ({ ...s, origen: v }))}>
              <SelectTrigger className="h-9 w-40" data-testid="waya-bloq-filtro-origen"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="auto">Automático</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="text-[11px] text-slate-500 block mb-1">Buscar (CVU / CUIT / nombre)</label>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={f.q} onChange={(e) => setF(s => ({ ...s, q: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && load()} placeholder="Buscar…" className="h-9 pl-8" data-testid="waya-bloq-filtro-q" />
            </div>
          </div>
          <Button size="sm" onClick={load} disabled={loading} className="bg-teal-600 hover:bg-teal-700 text-white h-9" data-testid="waya-bloq-aplicar">
            {loading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <RefreshCw size={14} className="mr-1.5" />} Aplicar
          </Button>
        </div>

        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
          <span className="text-[11px] text-slate-400 mr-1">Exportar:</span>
          <Button size="sm" variant="outline" onClick={() => exportar('xlsx')} disabled={!!exporting || !rows.length} data-testid="waya-bloq-export-xlsx">
            {exporting === 'xlsx' ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <FileSpreadsheet size={14} className="mr-1.5 text-emerald-600" />} Excel
          </Button>
          <Button size="sm" variant="outline" onClick={() => exportar('csv')} disabled={!!exporting || !rows.length} data-testid="waya-bloq-export-csv">
            {exporting === 'csv' ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <FileDown size={14} className="mr-1.5 text-slate-500" />} CSV
          </Button>
          <Button size="sm" variant="outline" onClick={() => exportar('pdf')} disabled={!!exporting || !rows.length} data-testid="waya-bloq-export-pdf">
            {exporting === 'pdf' ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <FileText size={14} className="mr-1.5 text-rose-600" />} PDF
          </Button>
        </div>
      </Card>

      {/* Tabla */}
      {loading ? (
        <div className="text-center text-slate-500 py-14"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : rows.length === 0 ? (
        <div className="text-center text-slate-400 py-14" data-testid="waya-bloq-empty">
          <Ban className="h-9 w-9 mx-auto mb-2 opacity-40" />
          No hay bloqueos ni cierres registrados para los filtros seleccionados.
        </div>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="waya-bloq-tabla">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2.5 font-medium whitespace-nowrap">Fecha</th>
                  <th className="text-left px-3 py-2.5 font-medium">Tipo</th>
                  <th className="text-left px-3 py-2.5 font-medium">CVU</th>
                  <th className="text-left px-3 py-2.5 font-medium">CUIT</th>
                  <th className="text-left px-3 py-2.5 font-medium">Nombre</th>
                  <th className="text-left px-3 py-2.5 font-medium">Origen</th>
                  <th className="text-left px-3 py-2.5 font-medium">Regla(s)</th>
                  <th className="text-left px-3 py-2.5 font-medium">Usuario</th>
                  <th className="text-center px-3 py-2.5 font-medium">Resultado</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`waya-bloq-row-${i}`}>
                    <td className="px-3 py-2.5 text-slate-600 dark:text-slate-300 whitespace-nowrap">{fechaCorta(r.fecha)}</td>
                    <td className="px-3 py-2.5">
                      <Badge className={r.es_cierre ? 'bg-red-600 text-white border-0' : 'bg-blue-600 text-white border-0'}>
                        {r.es_cierre ? 'Cierre definitivo' : 'Bloqueo preventivo'}
                      </Badge>
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-600 dark:text-slate-300">{r.cvu || '—'}</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-600 dark:text-slate-300">{r.cuit || '—'}</td>
                    <td className="px-3 py-2.5 text-slate-700 dark:text-slate-200">{r.nombre || '—'}</td>
                    <td className="px-3 py-2.5">
                      <Badge className="bg-slate-100 text-slate-600 border-0">{r.origen}</Badge>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-500 max-w-[220px] truncate" title={r.reglas}>{r.reglas || '—'}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-500">{r.usuario || '—'}</td>
                    <td className="px-3 py-2.5 text-center"><ResultadoBadge v={r.resultado} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default WayaBloqueosHistorial;
